﻿import React, { useEffect, useMemo, useState } from 'react'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, Text } from '../../components/ui'
import type { SupplierQuote } from '../../types/supplierQuotes'
import {
  formatCurrency,
  formatQuoteNumber,
  formatQuoteSelectLabel,
  quoteTotal,
  statusLabel,
} from './propostasUi'
import { formatRequestNumber } from './solicitacaoUi'

type Props = {
  label: string
  quotes: SupplierQuote[]
  value: string
  onChange: (quoteId: string) => void
  disabled?: boolean
}

const PropostaPickerField: React.FC<Props> = ({
  label,
  quotes,
  value,
  onChange,
  disabled = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [pendingId, setPendingId] = useState(value)

  const selected = useMemo(
    () => quotes.find((quote) => quote.id === value) || null,
    [quotes, value]
  )

  useEffect(() => {
    if (!modalOpen) return
    setPendingId(value)
    setSearch('')
  }, [modalOpen, value])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return quotes.filter((row) => {
      if (row.status === 'descartada') return false
      if (!term) return true
      return formatQuoteSelectLabel(row).toLowerCase().includes(term)
    })
  }, [quotes, search])

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <div className="flex min-h-[2.5rem] flex-1 items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900">
          {selected ? (
            <div className="flex w-full items-start justify-between gap-2">
              <Text size="sm" className="text-gray-900 dark:text-white">
                {formatQuoteSelectLabel(selected)}
              </Text>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onChange('')}
                  className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                  title="Limpar orçamento"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : (
            <Text size="sm" color="muted">
              Nenhum orçamento selecionado
            </Text>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => setModalOpen(true)}
          className="inline-flex shrink-0 items-center gap-2"
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
          {selected ? 'Trocar' : 'Buscar orçamento'}
        </Button>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <div>
                <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                  Selecionar orçamento
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  Escolha o orçamento de fornecedor para vincular à nota.
                </Text>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por número, fornecedor ou título..."
                  className="pl-9"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3">
              {filtered.length === 0 ? (
                <Text color="muted" className="py-8 text-center">
                  Nenhum orçamento encontrado.
                </Text>
              ) : (
                <div className="space-y-2">
                  {filtered.map((row) => {
                    const active = pendingId === row.id
                    return (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => setPendingId(row.id)}
                        className={`w-full rounded-lg border px-3 py-3 text-left transition ${
                          active
                            ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                            : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <Text className="font-medium text-gray-900 dark:text-white">
                              {formatQuoteNumber(row.quote_number)} · {row.title}
                            </Text>
                            <Text size="sm" color="muted">
                              {row.supplier?.name || '—'}
                              {row.request?.request_number != null
                                ? ` · Sol. ${formatRequestNumber(row.request.request_number)}`
                                : ' · Avulsa'}
                              {` · ${statusLabel(row.status)}`}
                            </Text>
                          </div>
                          <Text size="sm" className="shrink-0 font-medium">
                            {formatCurrency(quoteTotal(row))}
                          </Text>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={!pendingId}
                onClick={() => {
                  onChange(pendingId)
                  setModalOpen(false)
                }}
              >
                Selecionar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PropostaPickerField
